import React from 'react';
import { Link } from 'react-router-dom';
import Section from '../components/Section';
import Card from '../components/Card';
import Button from '../components/Button';
import SEO from '../components/SEO';
import content from '../data/content.json';

const FastEiendom: React.FC = () => {
  const eiendomData = content.fastEiendom;
  const heroSection = eiendomData.content.find(sec => sec.section === 'Hero');
  const pricingSection = eiendomData.content.find(sec => sec.section === 'Pricing');
  const textImageSection = eiendomData.content.find(sec => sec.section === 'TextImage');
  const servicesSection = eiendomData.content.find(sec => sec.section === 'Services');
  const whenYouNeedSection = eiendomData.content.find(sec => sec.section === 'WhenYouNeed');
  const useCasesSection = eiendomData.content.find(sec => sec.section === 'UseCases');
  const lawyersSection = eiendomData.content.find(sec => sec.section === 'Lawyers');
  const localExpertiseSection = eiendomData.content.find(sec => sec.section === 'LocalExpertise');
  const faqSection = eiendomData.content.find(sec => sec.section === 'FAQ');
  const ctaFooterSection = eiendomData.content.find(sec => sec.section === 'CTAFooter');

  // Property-themed images already in the project.
  const imageMap: { [key: string]: string } = {
    'hero-eiendom': '/privateImages/loft.webp',
    'eiendom-sikker': '/privateImages/drammensveien2bilde.webp',
    'when-need-eiendom': '/privateImages/kjeller01.webp',
  };

  // Reuse existing lawyer photos.
  const photoMap: { [key: string]: string } = {
    'Tove Kristin Farstad': '/privateImages/tove.webp',
    'Thorleif G. Bruland': '/privateImages/throleif.webp',
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": ["Service", "LegalService"],
    "name": "Fast Eiendom - Advokatfirmaet Bruland Oslo",
    "provider": {
      "@type": "LegalService",
      "name": "Advokatfirmaet Bruland",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Drammensveien 82c",
        "addressLocality": "Oslo",
        "postalCode": "0271",
        "addressCountry": "NO"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "59.9136930",
        "longitude": "10.7021640"
      },
      "areaServed": {
        "@type": "City",
        "name": "Oslo"
      }
    },
    "serviceType": "Fast eiendom advokat Oslo",
    "areaServed": {
      "@type": "City",
      "name": "Oslo"
    },
    "description": "Juridisk bistand innen fast eiendom i Oslo: bolighandel, mangler og reklamasjon, eiendomstvister, sameie og naborett."
  };

  // FAQ structured data so Google understands the questions this page answers.
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": (faqSection?.faqItems || []).map((f: any) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.answer
      }
    }))
  };

  return (
    <>
      <SEO
        title="Advokat Fast Eiendom Oslo | Eiendomsadvokat – Advokatfirmaet Bruland"
        description="Eiendomsadvokat i Oslo. Vi bistår ved boligkjøp, mangler og reklamasjon, eiendomstvister, sameie og naborett. Ring 22 53 53 22 for veiledning."
        keywords="advokat eiendom oslo, eiendomsadvokat oslo, advokat fast eiendom, boligtvist advokat, mangler ved bolig, reklamasjon bolig, sameie advokat oslo, naborett advokat"
        canonicalUrl="/fast-eiendom"
        structuredData={[structuredData, faqStructuredData]}
      />

      {/* Hero Section - image left, text+bullets right */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              {heroSection?.image && imageMap[heroSection.image] ? (
                <img
                  src={imageMap[heroSection.image]}
                  alt="Advokat fast eiendom Oslo - bistand ved bolighandel, mangler og eiendomstvister"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              ) : (
                <div className="w-full h-96 bg-gradient-to-br from-primary-light to-primary rounded-lg" />
              )}
            </div>

            <div className="order-1 lg:order-2">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 font-serif text-gray-900">
                {heroSection?.title}
              </h1>
              <ul className="space-y-4 mb-8">
                {heroSection?.bulletPoints?.map((point, idx) => (
                  <li key={idx} className="flex items-start">
                    <svg className="w-6 h-6 text-accent mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-lg text-gray-700">{point}</span>
                  </li>
                ))}
              </ul>
              <a href={`tel:${heroSection?.phone?.replace(/\s/g, '')}`}>
                <Button size="lg">{heroSection?.ctaText}</Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <Section title={pricingSection?.title} background="gray" spacing="lg" centered container="narrow">
        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
          {pricingSection?.description}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-8">
          <a href={`tel:${pricingSection?.phone?.replace(/\s/g, '')}`}>
            <Button size="lg">Ring {pricingSection?.phone}</Button>
          </a>
          <Link to="/kontakt">
            <Button variant="outline" size="lg">Send oss mail</Button>
          </Link>
        </div>
      </Section>

      {/* Text + Image Section - text left, image right */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 font-serif text-gray-900">
                {textImageSection?.title}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {textImageSection?.content}
              </p>
            </div>
            <div>
              {textImageSection?.image && imageMap[textImageSection.image] ? (
                <img
                  src={imageMap[textImageSection.image]}
                  alt="Eiendomsadvokat Oslo - rådgivning ved kjøp, salg og tvister om fast eiendom"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              ) : (
                <div className="w-full h-96 bg-gradient-to-br from-primary-light to-primary rounded-lg" />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - grid */}
      <Section title={servicesSection?.title} background="gray" spacing="lg">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
          {servicesSection?.services?.map((service: any) => (
            <Card key={service.id} variant="feature">
              <h3 className="text-xl font-bold text-gray-900 mb-3 font-serif">
                {service.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {service.description}
              </p>
            </Card>
          ))}
        </div>
      </Section>

      {/* When You Need Section - image left, text right */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              {whenYouNeedSection?.image && imageMap[whenYouNeedSection.image] ? (
                <img
                  src={imageMap[whenYouNeedSection.image]}
                  alt="Advokat i Oslo gir råd i eiendomssaker - sameie, naborett og boligtvister"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              ) : (
                <div className="w-full h-96 bg-gradient-to-br from-primary-light to-primary rounded-lg" />
              )}
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 font-serif text-gray-900">
                {whenYouNeedSection?.title}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {whenYouNeedSection?.content}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section - grid */}
      <Section title={useCasesSection?.title} background="gray" spacing="lg">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {useCasesSection?.cases?.map((useCase: any) => (
            <Card key={useCase.id} hoverable>
              <h3 className="text-xl font-bold text-gray-900 mb-3 font-serif">
                {useCase.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {useCase.description}
              </p>
            </Card>
          ))}
        </div>
      </Section>

      {/* Lawyers Section */}
      <Section title={lawyersSection?.title} background="white" spacing="lg" centered>
        <p className="text-lg text-gray-600 mb-12 text-center max-w-3xl mx-auto">
          {lawyersSection?.description}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
          {lawyersSection?.profiles?.map((lawyer: any, idx: number) => (
            <div key={idx} className="flex flex-col items-center text-center">
              {photoMap[lawyer.name] ? (
                <img
                  src={photoMap[lawyer.name]}
                  alt={`Portrett av advokat ${lawyer.name}`}
                  className="w-48 h-48 rounded-full mb-6 object-cover border-4 border-gray-200"
                />
              ) : (
                <div className="w-48 h-48 rounded-full mb-6 bg-gradient-to-br from-gray-200 to-gray-300" />
              )}
              <h3 className="text-2xl font-bold text-gray-900 mb-2 font-serif">{lawyer.name}</h3>
              <p className="text-lg text-accent mb-4">{lawyer.title}</p>
              <p className="text-gray-600 leading-relaxed">
                {lawyer.description}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* Local Expertise Section */}
      <Section title={localExpertiseSection?.title} background="white" spacing="lg" container="narrow" centered>
        <p className="text-lg text-gray-600 leading-relaxed mb-6">
          {localExpertiseSection?.content}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch mt-8">
          <a href={`tel:${localExpertiseSection?.phone?.replace(/\s/g, '')}`} className="w-full sm:w-auto flex">
            <Button size="lg" className="w-full sm:w-auto min-w-[200px] flex-1">Ring {localExpertiseSection?.phone}</Button>
          </a>
          <Link to="/kontakt" className="w-full sm:w-auto flex">
            <Button variant="outline" size="lg" className="w-full sm:w-auto min-w-[200px] flex-1">Besøk oss i Oslo</Button>
          </Link>
        </div>
      </Section>

      {/* FAQ Section */}
      <Section title={faqSection?.title} background="gray" spacing="lg" container="narrow">
        <div className="mt-8 space-y-6">
          {faqSection?.faqItems?.map((item: any) => (
            <div key={item.id} className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2 font-serif">{item.question}</h3>
              <p className="text-gray-600 leading-relaxed">{item.answer}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Related areas & articles - internal links for topical clustering */}
      <Section title="Relatert" background="white" spacing="lg" container="narrow" centered>
        <ul className="space-y-3 text-lg">
          <li>
            <Link to="/arv-og-testament" className="text-primary hover:text-primary-dark hover:underline font-medium">
              Arv og testament – advokat i Oslo
            </Link>
          </li>
          <li>
            <Link to="/article/rad-dersom-sameiet-vurderer-salg-av-raloft" className="text-primary hover:text-primary-dark hover:underline font-medium">
              Råd dersom sameiet vurderer salg av råloft
            </Link>
          </li>
          <li>
            <Link to="/article/kjelleretasje-kan-vaere-uegnet-for-boligformal" className="text-primary hover:text-primary-dark hover:underline font-medium">
              Kjelleretasje kan være uegnet for boligformål
            </Link>
          </li>
        </ul>
      </Section>

      {/* CTA Footer Section */}
      <section className="bg-primary text-white text-center py-20">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 font-serif text-white">
            {ctaFooterSection?.title}
          </h2>
          <p className="text-xl mb-8 text-white/90">
            {ctaFooterSection?.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${ctaFooterSection?.phone?.replace(/\s/g, '')}`}>
              <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-primary">
                Ring {ctaFooterSection?.phone}
              </Button>
            </a>
            <a href={`mailto:${ctaFooterSection?.email}`}>
              <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-primary">
                Send oss mail
              </Button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default FastEiendom;
